import { useState, useRef } from 'react';
import { ZoomIn, ZoomOut, EyeOff, Eye, Printer, MonitorPlay } from 'lucide-react';
import LogoTechTennis from '../assets/techtennis_logo_hd.png';

const BracketView = ({ matches, participants, competition }: { matches: any[], participants: any[], competition?: any }) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [hideCompleted, setHideCompleted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Group matches by round
  const rounds: { [key: number]: any[] } = {};
  
  matches.forEach(m => {
    const r = m.round || 1;
    if (!rounds[r]) rounds[r] = [];
    rounds[r].push(m);
  });
  
  // Sort matches within each round by matchIndex
  Object.keys(rounds).forEach(r => {
    rounds[parseInt(r)].sort((a, b) => (a.matchIndex || 0) - (b.matchIndex || 0));
  });

  const roundKeys = Object.keys(rounds).map(Number).sort((a, b) => a - b);

  const matchGlobalIndices: Record<string, number> = {};
  let globalCounter = 1;
  roundKeys.forEach(r => {
    (rounds[r] || []).forEach((m: any) => {
      matchGlobalIndices[m.id] = globalCounter++;
    });
  });

  const getPlayerName = (id: string | null, isBye?: boolean, match?: any, isPlayer1?: boolean) => {
    if (!id || id === 'TBD') {
      if (isBye) return 'BYE';
      if (match && match.round > 1) {
        const prevRound = match.round - 1;
        const prevLocalIndex = (match.matchIndex || 0) * 2 + (isPlayer1 ? 0 : 1);
        const prevMatch = rounds[prevRound]?.find((m: any) => m.matchIndex === prevLocalIndex);
        if (prevMatch && prevMatch.id && matchGlobalIndices[prevMatch.id]) {
          return `Venc. Jogo ${matchGlobalIndices[prevMatch.id]}`;
        }
      }
      return 'A Definir';
    }
    const p = participants.find(part => part.id === id || part.userId === id);
    return p ? (p.user?.name || p.name || 'Jogador Desconhecido') : (isBye ? 'BYE' : 'A Definir');
  };

  const getPlayerPhoto = (id: string | null) => {
    if (!id) return null;
    const p = participants.find(part => part.id === id || part.userId === id);
    return p?.user?.photoUrl || p?.photoUrl || null;
  };

  if (roundKeys.length === 0) {
    return (
      <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.7)', padding: '40px' }}>
         <p>A chave não foi inicializada.</p>
      </div>
    );
  }

  const maxRound = roundKeys.length > 0 ? Math.max(...roundKeys) : 0;
  let visibleRoundKeys = roundKeys;
  if (hideCompleted) {
    const currentRound = roundKeys.find(r => rounds[r].some(m => m.status !== 'COMPLETED' && m.status !== 'FINISHED')) || maxRound;
    visibleRoundKeys = roundKeys.filter(r => r >= currentRound);
  }

  // Separate Third-Place matches
  const lastRoundKey = visibleRoundKeys[visibleRoundKeys.length - 1];
  let thirdPlaceMatches: any[] = [];
  if (lastRoundKey && rounds[lastRoundKey].length > 1) {
    const thirdMatchIndex = rounds[lastRoundKey].findIndex((m: any) => m.stage?.includes('3º') || m.stage?.toLowerCase().includes('terceiro') || m.isThirdPlace);
    if (thirdMatchIndex !== -1) {
       const thirdMatch = rounds[lastRoundKey].splice(thirdMatchIndex, 1)[0];
       thirdPlaceMatches = [thirdMatch];
    }
  }

  const getInitials = (name?: string) => {
    if (!name || name === '?') return '?';
    if (name === 'BYE') return '-';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const parseScore = (scoreStr?: string) => {
    if (!scoreStr) return [];
    if (typeof scoreStr !== 'string') return [];
    return scoreStr.split(' ')
      .filter((s: string) => !s.includes('W.O.'))
      .map((s: string) => {
        const match = s.match(/^(\d+)\/(\d+)(?:\((\d+)(?:-(\d+))?\))?$/);
        if (match) {
          return {
            p1: match[1],
            p2: match[2],
            tb1: match[3] || '',
            tb2: match[4] || '' // Not standard but kept for completeness
          };
        }
        return { p1: '', p2: '' };
      });
  };

  const formatDateTime = (dateStr: string | null, timeStr: string | null) => {
    if (!dateStr && !timeStr) return '';
    let result = '';
    if (dateStr) {
      const d = new Date(dateStr);
      // add timezone offset so it doesn't shift days if UTC
      const localDate = new Date(d.getTime() + Math.abs(d.getTimezoneOffset() * 60000));
      result += localDate.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' });
    }
    if (timeStr) {
      result += result ? ` às ${timeStr}` : timeStr;
    }
    return result;
  };

  const renderMatchBox = (match: any, globalMatchNum?: number, stageName?: string) => {
    const sets = parseScore(match.score);
    const isFinished = match.status === 'FINISHED';
    const p1Winner = isFinished && match.winnerId === match.player1Id;
    const p2Winner = isFinished && match.winnerId === match.player2Id;
    const hasWO = match.score?.includes('W.O.');
    
    return (
    <div key={match.id} style={{
      background: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '8px',
      padding: '8px 12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      position: 'relative',
      justifyContent: 'center',
      zIndex: 2 // above the lines
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', marginBottom: '2px' }}>
        <span>#{globalMatchNum !== undefined ? globalMatchNum : (match.matchIndex !== undefined ? match.matchIndex + 1 : '')} • {stageName || match.stage || ''} {match.location ? `• ${match.location}` : ''}</span>
        {hasWO && <span style={{ background: '#ef4444', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '0.6rem', fontWeight: 'bold' }}>W.O.</span>}
      </div>

      {/* Player 1 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: match.winnerId && !p1Winner && isFinished ? 0.5 : 1 }}>
        {getPlayerPhoto(match.player1Id) ? (
          <img src={getPlayerPhoto(match.player1Id)} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
        ) : (
          <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: 'white', flexShrink: 0 }}>
            {getInitials(getPlayerName(match.player1Id, !match.player1Id && isFinished, match, true))}
          </div>
        )}
        <span style={{ color: 'white', fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: p1Winner ? 'bold' : 'normal', flex: 1 }}>
          {getPlayerName(match.player1Id, !match.player1Id && isFinished, match, true)}
        </span>
        <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
           {sets.map((s, idx) => (
             <div key={idx} style={{ display: 'flex', alignItems: 'flex-start' }}>
               <span style={{ fontSize: '0.9rem', fontWeight: 'bold', color: p1Winner ? 'var(--primary-color)' : 'white' }}>{s.p1}</span>
               {s.tb1 && <span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.6)', marginLeft: '1px' }}>{s.tb1}</span>}
             </div>
           ))}
        </div>
      </div>
      
      <div style={{ minHeight: '1px', flexShrink: 0, background: 'rgba(255,255,255,0.2)', width: 'calc(100% + 24px)', margin: '0 -12px' }}></div>
      
      {/* Player 2 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: match.winnerId && !p2Winner && isFinished ? 0.5 : 1 }}>
        {getPlayerPhoto(match.player2Id) ? (
          <img src={getPlayerPhoto(match.player2Id)} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
        ) : (
          <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: 'white', flexShrink: 0 }}>
            {getInitials(getPlayerName(match.player2Id, !match.player2Id && isFinished, match, false))}
          </div>
        )}
        <span style={{ color: 'white', fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: p2Winner ? 'bold' : 'normal', flex: 1 }}>
          {getPlayerName(match.player2Id, !match.player2Id && isFinished, match, false)}
        </span>
        <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
           {sets.map((s, idx) => (
             <div key={idx} style={{ display: 'flex', alignItems: 'flex-start' }}>
               <span style={{ fontSize: '0.9rem', fontWeight: 'bold', color: p2Winner ? 'var(--primary-color)' : 'white' }}>{s.p2}</span>
               {s.tb2 && <span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.6)', marginLeft: '1px' }}>{s.tb2}</span>}
             </div>
           ))}
        </div>
      </div>
      
      {/* Footer */}
      {(match.date || match.time) && (
        <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', marginTop: '2px', textAlign: 'center' }}>
          {formatDateTime(match.date, match.time)}
        </div>
      )}
    </div>
  )};

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen().catch(err => console.log(err));
    } else {
      document.exitFullscreen();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }} ref={containerRef} className="bracket-print-container">
      {/* Controls */}
      <div className="hide-on-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '0 20px', marginBottom: '10px' }}>
        <button 
          onClick={toggleFullscreen}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', width: '36px', height: '36px', borderRadius: '8px', cursor: 'pointer' }}
          title="Modo Apresentação"
        >
          <MonitorPlay size={18} />
        </button>
        <button 
          onClick={() => window.print()}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--primary-color)', color: 'black', border: 'none', width: '36px', height: '36px', borderRadius: '8px', cursor: 'pointer' }}
          title="Imprimir"
        >
          <Printer size={18} />
        </button>
        <button 
          onClick={() => setHideCompleted(!hideCompleted)}
          className="hide-on-print"
          style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
        >
          {hideCompleted ? <Eye size={16} /> : <EyeOff size={16} />}
          {hideCompleted ? 'Mostrar Tudo' : 'Ocultar Concluídas'}
        </button>
        <button 
          onClick={() => setZoomLevel(prev => Math.max(0.5, prev - 0.2))}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', width: '36px', height: '36px', borderRadius: '8px', cursor: 'pointer' }}
          title="Zoom Out"
        >
          <ZoomOut size={18} />
        </button>
        <button 
          onClick={() => setZoomLevel(prev => Math.min(2, prev + 0.2))}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', width: '36px', height: '36px', borderRadius: '8px', cursor: 'pointer' }}
          title="Zoom In"
        >
          <ZoomIn size={18} />
        </button>
      </div>

      <div style={{ padding: '20px', minHeight: '500px' }}>
        
        {/* Print Header */}
        <div className="show-on-print" style={{ 
          display: 'none', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '40px',
          paddingBottom: '20px',
          borderBottom: '2px solid #ccc'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            {competition?.imageUrl && <img src={competition.imageUrl} alt="Logo Torneio" style={{ width: 80, height: 80, borderRadius: '8px', objectFit: 'cover' }} />}
            <div>
              <h1 style={{ margin: '0 0 8px 0', fontSize: '24px', color: 'black' }}>{competition?.name || 'Chaveamento'}</h1>
              {competition?.startDate && (
                <div style={{ fontSize: '14px', color: '#555' }}>
                  {new Date(competition.startDate).toLocaleDateString('pt-BR')} 
                  {competition.endDate ? ` até ${new Date(competition.endDate).toLocaleDateString('pt-BR')}` : ''}
                </div>
              )}
            </div>
          </div>
          <img src={LogoTechTennis} alt="TechTennis" style={{ width: 140, objectFit: 'contain' }} />
        </div>

        <div className="bracket-zoom-wrapper" style={{ 
          display: 'flex', 
          gap: '60px', 
          minHeight: `${Math.max(500, Math.max(...Object.values(rounds).map((m: any) => m.length)) * 110)}px`,
          transform: `scale(${zoomLevel})`,
          transformOrigin: 'top left',
          transition: 'transform 0.2s'
        }}>
          {(() => {
            let globalMatchCounter = 1;
            return visibleRoundKeys.map((r, roundIndex) => {
              const isLastRound = roundIndex === visibleRoundKeys.length - 1;
              const matchesInRound = rounds[r];
              const pairs = [];
              for (let i = 0; i < matchesInRound.length; i += 2) {
                pairs.push([matchesInRound[i], matchesInRound[i + 1]]);
              }

              const stageName = isLastRound ? 'Final' : 
                  r === roundKeys[roundKeys.length - 2] ? 'Semifinal' : 
                  r === roundKeys[roundKeys.length - 3] ? 'Quartas de Final' : `Rodada ${r}`;

              return (
                <div key={r} style={{ display: 'flex', flexDirection: 'column', minWidth: '280px' }}>
                  <h3 style={{ color: 'white', textAlign: 'center', marginBottom: '20px', fontSize: '1rem', fontWeight: 600, height: '24px' }}>
                     {stageName}
                  </h3>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                {pairs.map((pair, idx) => (
                  <div key={idx} style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'space-around', 
                    position: 'relative', 
                    flex: 1
                  }}>
                    {/* Bracket Lines connecting this pair to the next round */}
                    {!isLastRound && pair[1] && (
                      <>
                        {/* Vertical line connecting top and bottom match */}
                        <div style={{ 
                          position: 'absolute', 
                          right: '-30px', 
                          top: '25%', // Mathematically perfect for 2 space-around items
                          bottom: '25%', 
                          width: '30px', 
                          borderRight: '2px solid rgba(255,255,255,0.2)', 
                          borderTop: '2px solid rgba(255,255,255,0.2)', 
                          borderBottom: '2px solid rgba(255,255,255,0.2)',
                          borderTopRightRadius: '4px',
                          borderBottomRightRadius: '4px'
                        }}></div>
                        {/* Horizontal line going to the next round */}
                        <div style={{ 
                          position: 'absolute', 
                          right: '-60px', 
                          top: 'calc(50% - 1px)', 
                          width: '30px', 
                          borderTop: '2px solid rgba(255,255,255,0.2)' 
                        }}></div>
                      </>
                    )}
                    
                    {/* Single match connecting line (if no pair[1]) */}
                    {!isLastRound && !pair[1] && (
                      <div style={{ 
                        position: 'absolute', 
                        right: '-60px', 
                        top: 'calc(50% - 1px)', 
                        width: '60px', 
                        borderTop: '2px solid rgba(255,255,255,0.2)' 
                      }}></div>
                    )}

                    {pair.map(match => {
                      if (!match) return null;
                      const currentMatchNum = globalMatchCounter++;
                      return renderMatchBox(match, currentMatchNum, stageName);
                    })}
                  </div>
                ))}
              </div>
            </div>
          );
          });
        })()}
        </div>
      </div>
      
      {thirdPlaceMatches.length > 0 && (
        <div style={{ padding: '0 20px', marginBottom: '40px' }}>
          <h3 style={{ color: 'white', fontSize: '1.2rem', marginBottom: '16px' }}>Disputa de 3º Lugar</h3>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            {thirdPlaceMatches.map((m: any) => (
              <div key={m.id} style={{ minWidth: '280px' }}>
                {renderMatchBox(m, undefined, 'Disputa de 3º Lugar')}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BracketView;
